import { useState } from 'react';
import { Send, CheckCircle, AlertCircle, HelpCircle } from 'lucide-react';
import PrivacyConsent from '../forms/PrivacyConsent';
import { facebookPixelEvents } from '../../utils/facebookPixel';

interface SignUpForWebinarProps {
    onSuccess?: () => void;
}

export default function SignUpForWebinar({ onSuccess }: SignUpForWebinarProps) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        
        const formData = new FormData(event.currentTarget);
        const hutk = document.cookie.replace(/(?:(?:^|.*;\s*)hubspotutk\s*\=\s*([^;]*).*$)|^.*$/, "$1");
        
        try {
            // Track webinar registration
            facebookPixelEvents.lead({
                content_name: 'Webinar Registration',
                content_category: 'webinar',
                content_type: 'registration'
            });

            const response = await fetch('https://api.hsforms.com/submissions/v3/integration/submit/141794537/e0044a1f-2110-4d52-9f7d-95c67d9cb389', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fields: [
                        { 
                            objectTypeId: "0-1",
                            name: 'firstname', 
                            value: formData.get('firstName')?.toString().split(' ')[0]
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'lastname', 
                            value: formData.get('lastName')?.toString().split(' ').slice(1).join(' ') || "."
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'email', 
                            value: formData.get('email') 
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'phone', 
                            value: formData.get('phone') 
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'role',
                            value: formData.get('role') 
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'homeschool_type',
                            value: formData.get('homeschool_type') 
                        },
                        { 
                            objectTypeId: "0-1",
                            name: 'student_age',
                            value: formData.get('student_age') 
                        }
                    ],
                    context: {
                        pageUri: window.location.href,
                        pageName: document.title,
                        ...(hutk && { hutk })
                    },
                    legalConsentOptions: {
                        consent: {
                            consentToProcess: true,
                            text: "EmploSkills is committed to protecting and respecting your privacy, and we'll only use your personal information to administer your account and to provide the products and services you requested from us.",
                            communications: [
                                {
                                    value: Boolean(formData.get('marketingConsent')),
                                    subscriptionTypeId: 999,
                                    text: "I agree to receive communications from EmploSkills"
                                }
                            ]
                        }
                    }
                })
            });

            if (!response.ok) throw new Error('Submission failed');
            
            setSubmitStatus('success');
            if (onSuccess) {
                setTimeout(onSuccess, 2000);
            }

            // Track complete registration
            facebookPixelEvents.completeRegistration({
                content_name: 'Webinar Registration Complete',
                content_category: 'webinar',
                content_type: 'registration_success'
            });
        } catch (error) {
            console.error('Error:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitStatus === 'success') {
        return (
            <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
                <p className="text-gray-600">
                    You're registered! Check your email for webinar details.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            {submitStatus === 'error' && (
                <div className="bg-red-50 p-4 rounded-md flex items-center text-red-700 mb-4">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    Something went wrong. Please try again.
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        First Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                        type="text"
                        id="firstName"
                        name="firstName"
                        required
                        className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                    />
                </div>
                <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name <span className="text-red-500">*</span>
                    </label>
                    <input 
                        type="text"
                        id="lastName"
                        name="lastName"
                        required
                        className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                    />
                </div>
            </div>
            
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                </label>
                <input 
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                />
            </div>

            <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                </label>
                <input 
                    type="tel"
                    id="phone"
                    name="phone"
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                />
            </div>

            <div>
                <div className="flex items-center gap-1">
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                        Role <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
                            <div className="relative">
                                To help us share the relevant information with you, please specify your role: Are you a HomeSchooling teacher or HomeSchooling parent, or other
                                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <select
                    id="role"
                    name="role"
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                    <option value="">Select Role</option>
                    <option value="Homeschool Parent">Homeschool Parent</option>
                    <option value="Homeschool Teacher">Homeschool Teacher</option>
                    <option value="Other">Other</option>
                </select>
            </div>

            <div>
            <div className="flex items-center gap-1">
                <label htmlFor="homeschool_type" className="block text-sm font-medium text-gray-700 mb-1">
                    Type of Homeschool 
                </label>
                <div className="relative group">
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
                            <div className="relative">
                            Please specify what type of HomeSchooling arrangement/type you have. This will help us share the relevant information with you
                                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <select
                    id="homeschool_type"
                    name="homeschool_type"
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                    <option value="">Select Type</option>
                    <option value="Individual household">Individual Homeschool</option>
                    <option value="Homeschooling Cooperative">Homeschooling Cooperative</option>
                    <option value="Micro School">Micro School</option>
                    <option value="Other">Other</option>
                </select>
            </div>
            <div>
            <div className="flex items-center gap-1">
                <label htmlFor="student_age" className="block text-sm font-medium text-gray-700 mb-1">
                    Student Age 
                </label>
                <div className="relative group">
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 hidden group-hover:block w-64 p-2 bg-gray-900 text-white text-sm rounded-md shadow-lg z-50">
                            <div className="relative">
                            Please specify the age of the student you are homeschooling. This will help us share the relevant information with you
                                <div className="absolute top-1/2 -translate-y-1/2 -left-1 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                        </div>
                    </div>
                </div>
                <select
                    id="student_age"
                    name="student_age"
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm p-2"
                >
                    <option value="">Select Age</option>
                    <option value="Middle School">Middle School</option>
                    <option value="High School">High School</option>
                    <option value="Middle and High School">Middle and High School</option>
                    <option value="Other">Other</option>
                </select>
            </div>
            

            <div className="mt-4">
                <PrivacyConsent onConsentChange={() => {}} />
            </div>

            <div className="mt-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-focus focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                    {isSubmitting && (
                        <Send className="animate-spin mr-2 h-4 w-4 text-white" />
                    )}
                    {isSubmitting ? 'Submitting...' : 'Sign Up'}
                </button>
            </div>
        </form>
    );
} 